/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, forwardRef, HostBinding, Input, Output, ViewChild } from '@angular/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { DisablableCheckbox } from '../disablable-checkbox';
import {EmptyCheckPipe} from "@ng-falcon/controls/checkbox/pseudo-checkbox/empty-check.pipe";
import { AsyncPipe } from "@angular/common";
import {MatIcon} from "@angular/material/icon";

@Component({
    selector: 'ngf-pseudo-checkbox',
    templateUrl: './pseudo-checkbox.component.html',
    styleUrls: ['./pseudo-checkbox.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EmptyCheckPipe, AsyncPipe, MatIcon],
    providers: [
        {
            provide: DisablableCheckbox,
            useExisting: forwardRef(() => PseudoCheckboxComponent)
        }
    ]
})
export class PseudoCheckboxComponent implements DisablableCheckbox {
  @Input()
  checked = false;

  @Input()
  indeterminate = false;

  @Input()
  ariaDescribedby?: string;

  @Input()
  ariaRequired?: boolean;

  @Input()
  ariaInvalid?: boolean;

  @HostBinding('attr.aria-label')
  ariaLabel: string | null

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('disabled')
  set isDisabled(value: boolean) {
    this.disabled.next(value);
  }

  @ViewChild('ref', { read: ElementRef })
  private labelElement?: ElementRef<HTMLElement>;

  @Output()
  checkedChange = new EventEmitter<boolean>();
  @Output()
  checkedWithOptions = new EventEmitter<{ checked: boolean, shiftKey: boolean }>();

  private readonly disabled = new BehaviorSubject<boolean>(false);
  readonly disabled$ = this.disabled.pipe(distinctUntilChanged());

  toggle(event: MouseEvent): void {
    if (this.disabled.value) {
      return;
    }
    this.checked = !this.checked;
    this.checkedChange.emit(this.checked);
    this.checkedWithOptions.emit({checked: this.checked, shiftKey: event.shiftKey});

    // we need to call `preventDefault()`,
    // because if <ngf-checkbox> is inside <label> tag
    // then browser default behavior is triggering `click` event twice
    (event.currentTarget as HTMLElement).focus(); // Ensure focus remains on the button
    event.preventDefault();
  }

  get ariaLabelTxt(): string | undefined {
    if (this.ariaLabel) {
      return this.ariaLabel;
    }
    const textContent = this.labelElement?.nativeElement?.textContent?.trim();
    return textContent || undefined;
  }

}
